import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { localeLanguageCodes } from 'shared-locales';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getAccessStatus, getCourseLanguageVersionOptions } from 'lib/courses';
import { PageContent, PageHeader } from 'components/page';
import { LinkButton, LoadingIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { CoursesUserAction } from 'components/courses';
import {
  CourseCertificate,
  CourseChapters,
  CourseInfo,
  CoursePaths,
  CourseProgress
} from '.';

import type { CourseProgress as CourseProgressData } from 'shared-types';
import type { CourseInView } from 'components/courses/index/Courses';

const Course = () => {
  const { language, messages, user } = useReduxState();

  const { query } = useRouter();

  const { data, loading, updateData } = useApiData({
    url: `/admin/courses?slug=${query?.courseParams?.[0]}`
  });

  const course = data?.courses?.[0] as CourseInView;

  const { canBeOpened } = getAccessStatus({ course, user });

  const { data: userCourseData } = useApiData({
    onlyIf: Boolean(course && canBeOpened),
    url: `/admin/users/${user?.id}/courses/${course?.id}`
  });

  const progressData = userCourseData?.progressData as CourseProgressData;

  const availableLanguages = getCourseLanguageVersionOptions({ course });

  const defaultLanguageVersion = useMemo(
    () =>
      typeof query.languageVersion === 'string'
        ? query.languageVersion
        : course?.courseCompletion?.courseLanguageVersion ||
          localeLanguageCodes[language],
    [
      course?.courseCompletion?.courseLanguageVersion,
      language,
      query.languageVersion
    ]
  );

  const displayLanguageVersion =
    availableLanguages[defaultLanguageVersion] &&
    availableLanguages[defaultLanguageVersion].isPublished
      ? defaultLanguageVersion
      : course?.language;

  return (
    <div className="course">
      <PageHeader icon="book" simpleBack={true} title={messages.commonGoBack}>
        {course?.isOwnerOrSuperadmin ? (
          <LinkButton
            category="light"
            href={`/admin/courses/${course.slug}`}
            size="small"
            text={messages.commonShowAdminPage}
          />
        ) : null}
      </PageHeader>

      <PageContent>
        {loading ? (
          <LoadingIndicator />
        ) : !course ? (
          <div>{messages.commonCourseNotFound}</div>
        ) : (
          <div className="wrapper">
            <div className="main">
              <div className="info">
                <Box>
                  <div className="inner">
                    <CourseInfo
                      course={course}
                      courseCompletionLanguage={
                        course.courseCompletion?.courseLanguageVersion
                      }
                      displayPreviewLanguageVersion={displayLanguageVersion}
                      enableCourseCompletionLanguageSelection={
                        !course.courseCompletion ||
                        course.courseCompletion?.status === 'inprogress'
                      }
                      onLanguageVersionChange={updateData}
                    />

                    <div className="info-footer">
                      <CoursesUserAction course={course} />
                    </div>
                  </div>
                </Box>
              </div>

              <div className="chapters">
                <Box padding={0} title={messages.commonChapters}>
                  <CourseChapters
                    chapters={course.chapters}
                    course={course}
                    languageVersion={displayLanguageVersion}
                    progressData={progressData}
                  />
                </Box>
              </div>
            </div>

            <div className="side">
              {progressData ? (
                <CourseProgress course={course} progressData={progressData} />
              ) : null}

              {course.certificateAllowDownloading &&
              progressData?.completion?.status === 'success' ? (
                <CourseCertificate
                  course={course}
                  progressData={progressData}
                />
              ) : null}

              {course.paths?.length ? (
                <CoursePaths paths={course.paths} />
              ) : null}
            </div>
          </div>
        )}
      </PageContent>

      <style jsx>{`
        .wrapper {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }
        .main {
          flex-grow: 1;
        }
        .side {
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          gap: 30px;
          width: 100%;
        }
        .info-footer {
          margin-top: 30px;
        }
        .chapters {
          margin-top: 30px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .inner {
            padding: 10px;
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .wrapper {
            flex-direction: row;
          }
          .side {
            width: 360px;
          }
        }
      `}</style>
    </div>
  );
};

export default Course;
